import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import type { Horse } from "./types";

export interface UseHorsesResult {
  horses: Horse[];
  loading: boolean;
  error: Error | null;
}

export function useHorses(yardId: string | null | undefined): UseHorsesResult {
  const [horses, setHorses] = useState<Horse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!yardId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "yards", yardId, "horses"),
      orderBy("name", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setHorses(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Horse, "id">) }))
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [yardId]);

  return { horses, loading, error };
}

export type NewHorseInput = Omit<Horse, "id" | "createdAt" | "createdBy">;

export async function createHorse(
  yardId: string,
  userId: string,
  horse: NewHorseInput
): Promise<string> {
  const ref = await addDoc(collection(db, "yards", yardId, "horses"), {
    ...horse,
    createdBy: userId,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export type HorseUpdate = Partial<Omit<Horse, "id" | "createdAt" | "createdBy">>;

export async function updateHorse(
  yardId: string,
  horseId: string,
  updates: HorseUpdate
): Promise<void> {
  const ref = doc(db, "yards", yardId, "horses", horseId);
  await updateDoc(ref, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// Firestore doesn't cascade subcollection deletes — pull all dailyLogs
// first and batch-delete them alongside the horse doc.
export async function deleteHorse(yardId: string, horseId: string): Promise<void> {
  const logsSnap = await getDocs(
    collection(db, "yards", yardId, "horses", horseId, "dailyLogs")
  );
  const batch = writeBatch(db);
  logsSnap.docs.forEach((d) => batch.delete(d.ref));
  batch.delete(doc(db, "yards", yardId, "horses", horseId));
  await batch.commit();
}
